import React, { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Tooltip,
  Box,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import { formatCurrency, getRiskBinLabel } from "../../helpers";
import RiskDescriptionContent from "../../shared/riskDescription/RiskDescriptionContent";
import { hasRiskDescriptionContent } from "../../../shared/riskDescriptionDict";
import StreetViewAction from "../../shared/StreetViewAction";

import RiskLabel from "./riskLabel";

const PropertyCard = memo(function PropertyCard({
  address,
  formattedAddress,
  capitalAtRisk,
  contractPrice,
  fireRisk,
  floodRisk,
  earthquakeRisk,
  fireBin,
  floodBin,
  earthquakeBin,
  renewalId,
  contractId,
  certainAddress,
  fire,
  flood,
  earthquake,
  floodDescription,
  fireDescription,
  earthquakeDescription,
  fireDescriptionDict,
  floodDescriptionDict,
  earthquakeDescriptionDict,
  fire_description_dict,
  flood_description_dict,
  earthquake_description_dict,
  showDescriptionsIcon,
  contentsCapitalAtRisk,
  propertyRiskType,
  earthquakeCoverage,
  floor,
  lat,
  lng,
  onClick,
}) {
  const { t } = useTranslation();
  const [showDescriptions, setShowDescriptions] = useState(false);

  const effectiveFireDescriptionDict =
    fireDescriptionDict || fire_description_dict || fire?.description_dict;
  const effectiveFloodDescriptionDict =
    floodDescriptionDict || flood_description_dict || flood?.description_dict;
  const effectiveEarthquakeDescriptionDict =
    earthquakeDescriptionDict ||
    earthquake_description_dict ||
    earthquake?.description_dict;
  const effectiveFireDescription = fireDescription || fire?.description;
  const effectiveFloodDescription = floodDescription || flood?.description;
  const effectiveEarthquakeDescription =
    earthquakeDescription || earthquake?.description;

  const hasDescriptions =
    hasRiskDescriptionContent({
      description: effectiveFireDescription,
      descriptionDict: effectiveFireDescriptionDict,
      riskType: "fire",
    }) ||
    hasRiskDescriptionContent({
      description: effectiveFloodDescription,
      descriptionDict: effectiveFloodDescriptionDict,
      riskType: "flood",
    }) ||
    hasRiskDescriptionContent({
      description: effectiveEarthquakeDescription,
      descriptionDict: effectiveEarthquakeDescriptionDict,
      riskType: "earthquake",
    });

  const handleExpandClick = (e) => {
    e.stopPropagation(); // Prevent card click
    setShowDescriptions(!showDescriptions);
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2,
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #eee",
        boxShadow: "none",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        position: "relative",
        "&:hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.02)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <StreetViewAction latitude={lat} longitude={lng} />

        {/* Warning icon for uncertain address */}
        {!certainAddress && (
          <Tooltip
            title={t("portfolioMap.addressNotVerified")}
            arrow
            placement="left"
          >
            <Box sx={{ cursor: "help" }} onClick={(e) => e.stopPropagation()}>
              <InfoIcon color="warning" sx={{ fontSize: 18, opacity: 0.8 }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      <CardContent sx={{ padding: "0px !important" }}>
        {/* Address */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.25,
            mb: 0.5,
            pr: "56px",
            letterSpacing: 0.2,
          }}
        >
          {address || t("common.noData")}
        </Typography>

        {formattedAddress && (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            {formattedAddress}
          </Typography>
        )}

        {propertyRiskType ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {propertyRiskType}
            </Typography>
          </Stack>
        ) : null}

        {floor && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("forms.floor")}
            </Typography>
            <Typography variant="body1">{floor}</Typography>
          </Stack>
        )}

        {/* Capital at Risk */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("portfolioMap.capitalAtRisk")}
          </Typography>
          <Typography variant="body1">
            {formatCurrency(capitalAtRisk)}
          </Typography>
        </Stack>

        {contentsCapitalAtRisk ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("portfolioMap.contentsCapitalAtRisk")}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(contentsCapitalAtRisk)}
            </Typography>
          </Stack>
        ) : null}

        {/* Contract Price */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("portfolioMap.contractPrice")}
          </Typography>
          <Typography variant="body1">
            {formatCurrency(contractPrice)}
          </Typography>
        </Stack>

        {renewalId && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("portfolioMap.renewalId")}
            </Typography>
            <Typography variant="body1">{renewalId}</Typography>
          </Stack>
        )}

        {contractId && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("portfolioMap.contractId")}
            </Typography>
            <Typography variant="body1">{contractId}</Typography>
          </Stack>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Risk row */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <RiskLabel
            label={t("risks.fire")}
            value={fireRisk}
            bin={fireBin}
            tooltip={t("portfolioMap.riskTooltip", {
              risk: t("risks.fire"),
              bin: getRiskBinLabel(fireBin, t),
            })}
          />
          <RiskLabel
            label={t("risks.flood")}
            value={floodRisk}
            bin={floodBin}
            tooltip={t("portfolioMap.riskTooltip", {
              risk: t("risks.flood"),
              bin: getRiskBinLabel(floodBin, t),
            })}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
            {/* Earthquake coverage icon */}
            {earthquakeCoverage && (
              <Tooltip
                title={t("portfolioMap.earthquakeCoverage")}
                arrow
                placement="left"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "help",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ShieldIcon
                    color="success"
                    sx={{ fontSize: 18, opacity: 0.8 }}
                  />
                </Box>
              </Tooltip>
            )}

            <RiskLabel
              label={t("risks.earthquake")}
              value={earthquakeRisk}
              bin={earthquakeBin}
              tooltip={t("portfolioMap.riskTooltip", {
                risk: t("risks.earthquake"),
                bin: getRiskBinLabel(earthquakeBin, t),
              })}
            />
          </Box>

          {showDescriptionsIcon && hasDescriptions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                marginLeft: "auto !important",
              }}
              onClick={handleExpandClick}
            >
              <IconButton
                size="small"
                sx={{
                  transform: showDescriptions
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                  p: 0.5,
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Stack>

        {/* Expandable Risk Descriptions */}
        {hasDescriptions && (
          <>
            <Collapse in={showDescriptions} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.5, pl: 0.5 }}>
                {hasRiskDescriptionContent({
                  description: effectiveFireDescription,
                  descriptionDict: effectiveFireDescriptionDict,
                  riskType: "fire",
                }) && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {t("portfolioMap.fireRiskSection")}
                    </Typography>
                    <RiskDescriptionContent
                      riskType="fire"
                      description={effectiveFireDescription}
                      descriptionDict={effectiveFireDescriptionDict}
                      dense
                    />
                  </Box>
                )}
                {hasRiskDescriptionContent({
                  description: effectiveFloodDescription,
                  descriptionDict: effectiveFloodDescriptionDict,
                  riskType: "flood",
                }) && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {t("portfolioMap.floodRiskSection")}
                    </Typography>
                    <RiskDescriptionContent
                      riskType="flood"
                      description={effectiveFloodDescription}
                      descriptionDict={effectiveFloodDescriptionDict}
                      risk={flood?.risk}
                      dense
                    />
                  </Box>
                )}
                {hasRiskDescriptionContent({
                  description: effectiveEarthquakeDescription,
                  descriptionDict: effectiveEarthquakeDescriptionDict,
                  riskType: "earthquake",
                }) && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {t("portfolioMap.earthquakeRiskSection")}
                    </Typography>
                    <RiskDescriptionContent
                      riskType="earthquake"
                      description={effectiveEarthquakeDescription}
                      descriptionDict={effectiveEarthquakeDescriptionDict}
                      dense
                    />
                  </Box>
                )}
              </Box>
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default PropertyCard;
